import React, { useEffect, useState } from 'react';
import {
  getAdminDashboard,
  type AdminDashboard,
  type AdminUserSummary,
} from '../api/admin-dashboard.api.js';
import AdminHeader from './AdminHeader.component.js';
import { AdminSearchUsersContent } from './AdminSearchUsers.component.js';
import UserLink from './UserLink.component.js';
import AdminNegativeExperiencePreview from './AdminNegativeExperiencePreview.component.js';
import { formatAdminDateTime } from '../utils/admin-date.js';
import { getAdminMessagesHref } from '../utils/member-url.js';

function adminMessagesUrl(
  userFrom?: AdminUserSummary,
  userTo?: AdminUserSummary,
) {
  return getAdminMessagesHref(userFrom, userTo);
}

export default function Admin() {
  const [dashboard, setDashboard] = useState<AdminDashboard>({
    negativeExperiences: [],
    threadVotes: [],
    topMessengers: [],
  });
  const [isDashboardLoading, setIsDashboardLoading] = useState(true);
  const [dashboardError, setDashboardError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function loadDashboard() {
      try {
        const data = await getAdminDashboard();

        if (isMounted) {
          setDashboard({
            negativeExperiences: data.negativeExperiences || [],
            threadVotes: data.threadVotes || [],
            topMessengers: data.topMessengers || [],
          });
          setDashboardError(null);
        }
      } catch {
        if (isMounted) {
          setDashboardError('Could not load dashboard activity.');
        }
      } finally {
        if (isMounted) {
          setIsDashboardLoading(false);
        }
      }
    }

    loadDashboard();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <>
      <AdminHeader />
      <div className="container admin-landing">
        <header className="admin-landing__hero">
          <h1 className="admin-landing__title">Admin Dashboard</h1>
          <a href="/admin/circles">Manage circles</a>
        </header>

        <div className="admin-landing__search">
          <AdminSearchUsersContent showHeading={false} />
        </div>

        <div className="row admin-dashboard-boxes">
          {dashboardError && (
            <div className="col-sm-12">
              <p className="text-danger">{dashboardError}</p>
            </div>
          )}
          <div className="col-sm-4">
            <section className="panel panel-default admin-dashboard-box">
              <div className="panel-heading">
                <h2 className="panel-title">Top 10 Messengers Last Week</h2>
              </div>
              <div className="panel-body">
                {isDashboardLoading && <p className="text-muted">Loading...</p>}
                {!isDashboardLoading &&
                  dashboard.topMessengers.length === 0 && (
                    <p className="text-muted">No messages last week.</p>
                  )}
                {!isDashboardLoading && dashboard.topMessengers.length > 0 && (
                  <table className="table table-condensed admin-dashboard-table">
                    <tbody>
                      {dashboard.topMessengers.map(
                        ({ messageCount, user }, index) => (
                          <tr
                            key={user && user._id ? user._id : `row-${index}`}
                          >
                            <td>
                              <UserLink user={user || {}} />
                            </td>
                            <td className="text-right">
                              {messageCount} messages
                            </td>
                          </tr>
                        ),
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            </section>
          </div>
          <div className="col-sm-4">
            <section className="panel panel-default admin-dashboard-box">
              <div className="panel-heading">
                <h2 className="panel-title">
                  <a href="/admin/reference-threads">
                    Last 10 Negative Thread Votes
                  </a>
                </h2>
              </div>
              <div className="panel-body">
                {isDashboardLoading && <p className="text-muted">Loading...</p>}
                {!isDashboardLoading && dashboard.threadVotes.length === 0 && (
                  <p className="text-muted">No negative thread votes found.</p>
                )}
                {!isDashboardLoading && dashboard.threadVotes.length > 0 && (
                  <table className="table table-condensed admin-dashboard-table">
                    <tbody>
                      {dashboard.threadVotes.map(
                        ({ _id, created, thread, userFrom, userTo }) => {
                          const messagesUrl = adminMessagesUrl(
                            userFrom,
                            userTo,
                          );

                          return (
                            <tr key={_id}>
                              <td>
                                <UserLink user={userFrom || {}} />
                                {' -> '}
                                <UserLink user={userTo || {}} />
                              </td>
                              <td className="text-right">
                                {messagesUrl ? (
                                  <a href={messagesUrl}>
                                    {formatAdminDateTime(created) === '—'
                                      ? thread
                                      : formatAdminDateTime(created)}
                                  </a>
                                ) : formatAdminDateTime(created) === '—' ? (
                                  thread
                                ) : (
                                  formatAdminDateTime(created)
                                )}
                              </td>
                            </tr>
                          );
                        },
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            </section>
          </div>
          <div className="col-sm-4">
            <section className="panel panel-default admin-dashboard-box">
              <div className="panel-heading">
                <h2 className="panel-title">Last 10 Negative Experiences</h2>
              </div>
              <div className="panel-body">
                {isDashboardLoading && <p className="text-muted">Loading...</p>}
                {!isDashboardLoading &&
                  dashboard.negativeExperiences.length === 0 && (
                    <p className="text-muted">No negative experiences found.</p>
                  )}
                {!isDashboardLoading &&
                  dashboard.negativeExperiences.length > 0 && (
                    <table className="table table-condensed admin-dashboard-table">
                      <tbody>
                        {dashboard.negativeExperiences.map(
                          ({
                            _id,
                            created,
                            feedbackPublic,
                            userFrom,
                            userTo,
                          }) => (
                            <tr key={_id}>
                              <td>
                                <UserLink user={userFrom || {}} />
                                {' -> '}
                                <UserLink user={userTo || {}} />
                              </td>
                              <td className="text-right">
                                <AdminNegativeExperiencePreview
                                  dateLabel={formatAdminDateTime(created)}
                                  feedbackPublic={feedbackPublic}
                                />
                              </td>
                            </tr>
                          ),
                        )}
                      </tbody>
                    </table>
                  )}
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}
